import moment from "moment-timezone";

/**
 * Debounce utility function to reduce frequent calls
 */
export function debounce(func, delay) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      try {
        func(...args);
      } catch (e) {
        console.error('Debounced function error:', e);
      }
    }, delay);
  };
}

/**
 * Format time value to two digits (e.g., 5 -> "05")
 */
export function formatTime(value) {
  try {
    if (typeof value !== "number" || isNaN(value)) {
      console.error("Invalid input to formatTime: expected a number, got", value);
      return "00";
    }
    return value < 10 ? `0${value}` : value;
  } catch (error) {
    console.error("Error in formatTime:", error);
    return "00";
  }
}

/**
 * Validate 12-hour time format (e.g., "09:30 AM")
 */
export const timeValidate = (timeString) => {
  try {
    if (typeof timeString !== "string") {
      console.error("Invalid input to timeValidate: expected a string, got", timeString);
      return false;
    }

    const timePattern = /^(1[0-2]|0[1-9]):([0-5][0-9]) [APap][Mm]$/;
    const isValidFormat = timePattern.test(timeString);
    return isValidFormat;

  } catch (error) {
    console.error("Error in timeValidate:", error);
    return false;
  }
};

/**
 * Convert date and time string to formatted date-time string
 */
export const covertTimeSatmp = (dateEdit, sT, tz) => {
  try {
    if (!dateEdit || !sT) {
      console.error("Invalid parameters in covertTimeSatmp. Received:", { dateEdit, sT, tz });
      return null;
    }

    const date = new Date(dateEdit);
    if (isNaN(date.getTime())) {
      console.error("Invalid date in covertTimeSatmp:", dateEdit);
      return null;
    }

    const timeString = sT;
    const datePart = date.toLocaleDateString("en-US");
    const timePart = timeString;

    // Combine the date and time strings
    const combinedDateTimeString = `${datePart} ${timePart}`;

    const dateObject = new Date(combinedDateTimeString);
    if (isNaN(dateObject.getTime())) {
      console.error("Invalid combined date-time in covertTimeSatmp:", combinedDateTimeString);
      return null;
    }

    // Set the locale to English
    const formattedDate = dateObject.toLocaleString("en-US");

    return formattedDate;

  } catch (error) {
    console.error("Error in covertTimeSatmp:", error);
    return null;
  }
};

/**
 * Validate time format (HH:mm:ss)
 */
export function validateTimeFormat(timeString) {
  try {
    if (typeof timeString !== "string") {
      console.error("Invalid input to validateTimeFormat: expected string, got", timeString);
      return false;
    }

    const timeRegex = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
    return timeRegex.test(timeString);

  } catch (error) {
    console.error("Error in validateTimeFormat:", error);
    return false;
  }
}

/**
 * Validate 12-hour time format with optional period (e.g., "09:30 AM")
 */
export function isValidTimeFormat(timeString) {
  try {
    const timeRegex = /^(0?[1-9]|1[0-2]):[0-5][0-9] [APMapm]{2}$/;
    return timeRegex.test(timeString);
  } catch (error) {
    console.error("Error in isValidTimeFormat:", error);
    return false; // Fallback to invalid
  }
}

/**
 * Parse time input string to formatted time
 */
export const parseTimeInput = (input) => {
  try {
    if (typeof input !== "string") {
      console.error("Invalid input type for parseTimeInput");
      return false;
    }

    input = input.trim().toLowerCase();
    let timeRegex = /^(\d{1,2}):?(\d{2})?\s*([ap]m?)?$/;
    let match = input.match(timeRegex);

    if (!match) {
      // Try fallback regex
      timeRegex = /^(\d{1,2}):?(\d{1,2})?\s*([ap]m?)?$/;
      match = input.match(timeRegex);
      if (!match) {
        console.error("Input did not match time format:", input);
        return false;
      }
    }

    let [, hour, minutes, period] = match;
    hour = hour.padStart(2, "0");
    minutes = minutes ? minutes.padStart(2, "0") : "00";
    period = period
      ? period.toUpperCase()
      : parseInt(hour, 10) < 12
        ? "AM"
        : "PM";

    if (parseInt(hour, 10) > 12) {
      console.error("Invalid hour value in time input:", hour);
      return false;
    }

    return `${hour}:${minutes} ${period}`;
  } catch (error) {
    console.error("Error in parseTimeInput:", error);
    return false; // Fallback to invalid
  }
};

/**
 * Format time string to standard format
 */
export const timeFormatChange = (time) => {
  try {
    const input = parseTimeInput(time);
    if (input) {
      const momentTime = moment(input, "hh:mm A");
      return momentTime.format("hh:mm A");
    }
    console.error("Invalid time input format in timeFormatChange:", time);
    return false;
  } catch (error) {
    console.error("Error in timeFormatChange:", error);
    return false;
  }
};

/**
 * Convert time string to standard 12-hour format
 */
export function convertToStandardTime(timeString) {
  try {
    const isPM = timeString.toLowerCase().includes("p");
    const hours = parseInt(timeString, 10);
    if (isNaN(hours)) {
      console.error("Invalid numeric value in convertToStandardTime:", timeString);
      return "Invalid format";
    }

    const standardTime = moment({ hour: hours % 12, minute: 0 }).format("hh:mm A");
    return isPM ? standardTime.replace("AM", "PM") : standardTime;
  } catch (error) {
    console.error("Error in convertToStandardTime:", error);
    return "Invalid format";
  }
}

/**
 * Validate custom time format (e.g., "9a", "12p")
 */
export function isValidCustomTimeFormat(timeString) {
  try {
    const customTimeRegex = /^(0?[1-9]|1[0-2])[apAP]$/;
    return customTimeRegex.test(timeString);
  } catch (error) {
    console.error("Error in isValidCustomTimeFormat:", error);
    return false;
  }
}

/**
 * Subtract one hour from a time string
 */
export const subOneHour = (startTime) => {
  try {
    if (!startTime) {
      console.error("subOneHour: startTime is empty or undefined");
      return "Invalid time";
    }

    const time = moment(startTime, "hh:mm A");
    if (!time.isValid()) {
      console.error("subOneHour: Invalid time format", startTime);
      return "Invalid time";
    }

    const newTime = time.subtract(1, "hour");
    return newTime.format("hh:mm A");
  } catch (error) {
    console.error("Error in subOneHour:", error);
    return "Invalid time";
  }
};

/**
 * Convert time format (e.g., "3:45p" to "03:45 PM")
 */
export const timeFomatChange2 = (time) => {
  try {
    if (!time || typeof time !== "string") {
      console.error("timeFomatChange2: Invalid input. Expected a non-empty string.");
      return false;
    }

    const input = time.trim().toLowerCase();
    let converted = "";

    // Check if the input matches the supported time format (e.g., "3:45p" or "11:30a")
    const timePattern = /^\d{1,2}:\d{2}[ap]$/i;
    if (timePattern.test(input)) {
      const [hour, minutes] = input.match(/\d+/g);
      const isPM = input.includes("p");
      let formattedHour = parseInt(hour, 10);

      if (isNaN(formattedHour)) {
        console.error("timeFomatChange2: Invalid hour extracted from input.", input);
        return false;
      }

      if (isPM && formattedHour !== 12) {
        formattedHour += 12;
      }
      if (!isPM && formattedHour === 12) {
        formattedHour = 0;
      }

      formattedHour = formattedHour % 12 || 12; // Convert 0 to 12 for 12-hour format

      converted = `${formattedHour.toString().padStart(2, "0")}:${minutes} ${isPM ? "PM" : "AM"}`;
    } else {
      console.error("timeFomatChange2: Invalid time format.", input);
      converted = false;
    }

    return converted;
  } catch (error) {
    console.error("Error in timeFomatChange2:", error);
    return false;
  }
};

/**
 * Split time across two days if it spans midnight
 */
export function splitTime(startDateTime, totalTime) {
  try {
    // Convert startDateTime to a moment object
    let start = moment(startDateTime, "M/D/YYYY, h:mm:ss A");
    if (!start.isValid()) {
      console.error("Invalid startDateTime format:", startDateTime);
      return null;
    }

    // Extract hours, minutes, and seconds from totalTime
    let [hours, minutes, seconds] = totalTime.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
      console.error("Invalid totalTime format:", totalTime);
      return null;
    }

    // Calculate stop time
    let stopTime = start.clone().add(hours, "hours").add(minutes, "minutes").add(seconds, "seconds");

    // Check if stop time is on the next day
    if (stopTime.isAfter(start.clone().endOf("day"))) {
      // Time left until the end of the start day
      let endOfDay = start.clone().endOf("day");
      let remainingTime = stopTime.diff(endOfDay, "seconds");

      let nextDayStart = endOfDay.clone().add(1, "second");
      let nextDayStop = nextDayStart.clone().add(remainingTime, "seconds");

      return {
        day1: {
          start: start.format("M/D/YYYY, h:mm:ss A"),
          stop: endOfDay.format("M/D/YYYY, h:mm:ss A")
        },
        day2: {
          start: nextDayStart.format("M/D/YYYY, h:mm:ss A"),
          stop: nextDayStop.format("M/D/YYYY, h:mm:ss A")
        }
      };
    } else {
      return {
        singleDay: {
          start: start.format("M/D/YYYY, h:mm:ss A"),
          stop: stopTime.format("M/D/YYYY, h:mm:ss A")
        }
      };
    }
  } catch (error) {
    console.error("Error in splitTime:", error);
    return null;
  }
}

/**
 * Filter dates and find extremes for current week
 */
export function filterAndFindExtremes(dates, appSettings) {
  try {
    if (!Array.isArray(dates)) {
      console.error("filterAndFindExtremes: Invalid dates array. Expected an array.");
      return { highestDate: null, leastDate: null };
    }

    if (dates.length === 0) {
      // Empty array is a valid initial state, not an error
      return { highestDate: null, leastDate: null };
    }

    const timeZone = global.config.getTimeZone();
    const dateObject = moment().tz(timeZone);
    const formattedDate = dateObject.format("YYYY-MM-DD HH:mm:ss Z");

    const currentDate = moment.tz(formattedDate, timeZone).toDate();

    let currentWeekStartDate = moment.tz(currentDate, timeZone).toDate();
    let currentWeekEndDate = moment.tz(currentWeekStartDate, timeZone).toDate();

    if (appSettings?.weekStartDay && appSettings.weekStartDay === "Monday") {
      currentWeekStartDate.setDate(currentDate.getDate() - ((currentDate.getDay() + 6) % 7));
      currentWeekStartDate.setHours(0, 0, 0, 0);

      currentWeekEndDate = moment.tz(currentWeekStartDate, timeZone).toDate();
      const getDateNew = moment(currentWeekEndDate).add(6, "days");
      currentWeekEndDate.setDate(new Date(getDateNew.format("YYYY-MM-DD")).getDate());
      currentWeekEndDate.setHours(23, 59, 59, 999);
    } else {
      currentWeekStartDate.setDate(currentDate.getDate() - currentDate.getDay());
      currentWeekStartDate.setHours(0, 0, 0, 0);

      currentWeekEndDate = moment.tz(currentWeekStartDate, timeZone).toDate();
      const getDateNew = moment(currentWeekEndDate).add(6, "days");
      currentWeekEndDate.setDate(new Date(getDateNew.format("YYYY-MM-DD")).getDate());
      currentWeekEndDate.setHours(23, 59, 59, 999);
    }

    const datesInCurrentWeek = dates.filter((date) => {
      try {
        const currentDateObj = moment.tz(date, timeZone).toDate();
        return currentDateObj >= currentWeekStartDate && currentDateObj <= currentWeekEndDate;
      } catch (err) {
        console.error("filterAndFindExtremes: Error parsing date:", date, err);
        return false;
      }
    });

    if (datesInCurrentWeek.length === 0) {
      console.warn("filterAndFindExtremes: No dates found in current week.");
      return { highestDate: null, leastDate: null };
    }

    const highestDate = moment(Math.max(...datesInCurrentWeek.map(d => new Date(d).getTime()))).toDate();
    const leastDate = moment(Math.min(...datesInCurrentWeek.map(d => new Date(d).getTime()))).toDate();

    return { highestDate, leastDate };

  } catch (error) {
    console.error("filterAndFindExtremes: Unexpected error:", error);
    return { highestDate: null, leastDate: null };
  }
}

/**
 * Find extremes of last week
 */
export function findExtremesOfLastWeek(dates, appSettings) {
  try {
    if (!Array.isArray(dates)) {
      console.error("findExtremesOfLastWeek: Invalid dates array. Expected an array.");
      return { highestLastWeekDate: null, leastLastWeekDate: null };
    }

    if (dates.length === 0) {
      // Empty array is a valid initial state, not an error
      return { highestLastWeekDate: null, leastLastWeekDate: null };
    }

    const currentDate = moment().tz(global.config.getTimeZone()).toDate();
    const dateObject = moment().tz(global.config.getTimeZone());
    const formattedDate = dateObject.format("YYYY-MM-DD");

    let lastWeekStartDate = moment.tz(formattedDate, global.config.getTimeZone()).toDate();

    if (appSettings?.weekStartDay && appSettings.weekStartDay === "Monday") {
      // Monday to Sunday logic
      lastWeekStartDate.setDate(
        currentDate.getDate() - ((currentDate.getDay() + 6) % 7) - 7
      );
    } else {
      // Sunday to Saturday logic
      lastWeekStartDate.setDate(
        currentDate.getDate() - currentDate.getDay() - 7
      );
    }

    lastWeekStartDate.setHours(0, 0, 0, 0);

    let lastWeekEndDate = moment.tz(lastWeekStartDate, global.config.getTimeZone()).toDate();
    lastWeekEndDate.setDate(lastWeekStartDate.getDate() + 6);
    lastWeekEndDate.setHours(23, 59, 59, 999);

    // Filter dates that fall in the last week range
    const datesInLastWeek = dates.filter((date) => {
      try {
        const d = new Date(date);
        return d >= lastWeekStartDate && d <= lastWeekEndDate;
      } catch (err) {
        console.error("findExtremesOfLastWeek: Error parsing date:", date, err);
        return false;
      }
    });

    if (datesInLastWeek.length === 0) {
      console.warn("findExtremesOfLastWeek: No dates found in last week range.");
      return { highestLastWeekDate: null, leastLastWeekDate: null };
    }

    const highestLastWeekDate = moment.tz(
      Math.max(...datesInLastWeek.map(d => new Date(d).getTime())),
      global.config.getTimeZone()
    ).toDate();

    const leastLastWeekDate = moment.tz(
      Math.min(...datesInLastWeek.map(d => new Date(d).getTime())),
      global.config.getTimeZone()
    ).toDate();

    highestLastWeekDate.setHours(0, 0, 0, 0);
    leastLastWeekDate.setHours(0, 0, 0, 0);

    return { highestLastWeekDate, leastLastWeekDate };

  } catch (error) {
    console.error("findExtremesOfLastWeek: Unexpected error:", error);
    return { highestLastWeekDate: null, leastLastWeekDate: null };
  }
}

/**
 * Find extremes by week
 */
export function findExtremesByWeek(dates, appSettings) {
  try {
    if (!Array.isArray(dates)) {
      console.error("findExtremesByWeek: Invalid dates array. Expected an array.");
      return [];
    }

    if (dates.length === 0) {
      // Empty array is a valid initial state, not an error
      return [];
    }

    const datesByWeek = {};

    for (const date of dates) {
      try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) {
          console.warn("findExtremesByWeek: Skipping invalid date:", date);
          continue;
        }

        const weekStartDateFil = moment.tz(date, global.config.getTimeZone());
        const weekStartDateF = weekStartDateFil.format("YYYY-MM-DD");
        const weekStartDate = moment.tz(weekStartDateF, global.config.getTimeZone()).toDate();

        if (appSettings?.weekStartDay && appSettings.weekStartDay === "Monday") {
          weekStartDate.setDate(dateObj.getDate() - ((dateObj.getDay() + 6) % 7));
        } else {
          weekStartDate.setDate(dateObj.getDate() - dateObj.getDay());
        }
        weekStartDate.setHours(0, 0, 0, 0);

        const weekStartDateString = weekStartDate.toISOString();

        if (!datesByWeek[weekStartDateString]) {
          datesByWeek[weekStartDateString] = [];
        }
        datesByWeek[weekStartDateString].push(dateObj);

      } catch (err) {
        console.error("findExtremesByWeek: Error processing date:", date, err);
      }
    }

    const extremesByWeek = [];

    for (const [week, weekDates] of Object.entries(datesByWeek)) {
      try {
        const maxDate = new Date(Math.max(...weekDates.map(d => d.getTime())));
        const minDate = new Date(Math.min(...weekDates.map(d => d.getTime())));

        const highestWeekDate = moment.tz(maxDate, global.config.getTimeZone()).toDate();
        const leastDate = moment.tz(minDate, global.config.getTimeZone()).toDate();

        extremesByWeek.push({ highestWeekDate, leastDate });

      } catch (err) {
        console.error("findExtremesByWeek: Error calculating extremes for week:", week, err);
      }
    }

    return extremesByWeek;

  } catch (error) {
    console.error("findExtremesByWeek: Unexpected error:", error);
    return [];
  }
}

/**
 * Get moment date formatted as YYYY-MM-DD
 */
export function getMomentDate(date, format) {
  const dateObject = moment(date, format);
  return dateObject.format("YYYY-MM-DD");
}

/**
 * Find least date from data array based on input date
 */
export const findLeastDate = (inputDate, data) => {
  let leastDate = null;
  data.forEach((item) => {
    const startDate = moment.tz(item.leastDate, global.config.getTimeZone()).toDate();
    const endDate = moment.tz(item.highestWeekDate, global.config.getTimeZone()).toDate();
    if (inputDate >= startDate && inputDate <= endDate) {
      leastDate = item.leastDate;
    }
  });
  return leastDate;
};

/**
 * Find extreme date from data array based on input date
 */
export const findExtremeDate = (inputDate, data) => {
  let highestDate = null;

  data.forEach((item) => {
    const startDate = new Date(item.leastDate);
    const endDate = new Date(item.highestWeekDate);
    if (inputDate >= startDate && inputDate <= endDate) {
      highestDate = item.highestWeekDate;
    }
  });
  return highestDate;
};

/**
 * Validate timer start data
 */
export const validateTimerStartData = (data) => {
  const { taskName, projectName, sprintId } = data;
  const errors = [];
  
  if (!taskName) errors.push("'Task'");
  if (!projectName) errors.push("'Project'");
  if (!sprintId) errors.push("Sprint");
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Build timer start post data
 */
export const buildTimerStartPostData = (data) => {
  const {
    taskName,
    projectName,
    projectId,
    tag,
    tagId,
    startTime,
    selectedUser,
    client,
    clientId,
    isBillable,
    sprintName,
    sprintId
  } = data;

  const postData = {
    task: taskName,
    projectName: projectName,
    project: projectId,
    tagName: tag,
    tag: tagId,
    startTime: startTime,
    user: selectedUser,
    client: client,
    clientId: clientId,
    billable: isBillable,
    tz: global.config.getTimeZone(),
    date: startTime,
  };

  if (sprintId) {
    postData.sprintName = sprintName;
    postData.sprint = sprintId;
  }

  return postData;
};

/**
 * Validate timer stop data
 */
export const validateTimerStopData = (data) => {
  const { user, taskName, projectId, projectName, clientId, client, sprintId, sprintName, isBillable } = data;
  const errors = [];

  if (!user?._id) errors.push("User ID is missing.");
  if (!taskName) errors.push("Task name is required.");
  if (!projectId || !projectName) errors.push("Project is required.");
  if (!clientId || !client) errors.push("Client is required.");
  if (!sprintId || !sprintName) errors.push("Sprint is required.");
  if (typeof isBillable === "undefined") errors.push("Billable status is required.");

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Build timer stop post data
 */
export const buildTimerStopPostData = (data) => {
  const {
    startTime,
    endTime,
    user,
    taskName,
    projectName,
    projectId,
    tag,
    tagId,
    client,
    clientId,
    isBillable,
    sprintName,
    sprintId,
    runTask
  } = data;

  const basePostData = {
    startTime: startTime,
    endTime: endTime,
    user: user._id,
    task: taskName,
    projectName: projectName,
    project: projectId,
    tagName: tag,
    tag: tagId,
    client: client,
    clientId: clientId,
    billable: isBillable,
    tz: global.config.getTimeZone(),
    date: endTime,
  };

  if (sprintId) {
    basePostData.sprintName = sprintName;
    basePostData.sprint = sprintId;
  }

  const postData = { ...basePostData };
  if (runTask) {
    postData.id = runTask;
  }

  return {
    postData,
    apiUrl: runTask ? "/api/time-track/stop-timer" : "/api/time-track/stop-timer/version2"
  };
};

/**
 * Get keys to remove from localStorage for timer cleanup
 */
export const getTimerCleanupKeys = () => {
  return [
    "startTime", "project", "task", "isRunning", "runningTask",
    "tag", "tagId", "projectId", "sprintName", "sprintId",
    "client", "clientId", "isBillable"
  ];
};

/**
 * Get current time in timezone format
 */
export const getCurrentTime = () => {
  return moment()
    .tz(global.config.getTimeZone())
    .format("YYYY-MM-DD HH:mm:ss");
};

